const SHAPES = ['●', '◆', '✦', '▲', '⬟', '✚', '♥', '■'];

/** Build presentation from the public cell projection only. Concealed marble
 * colour is absent from style, text, dataset and accessible descendants. */
export function progressiveCellArtwork(ctx, state, cell) {
  if (!['ball-sort', 'pebble-balance'].includes(state.gameId)) return null;
  const marble = state.gameId === 'ball-sort';
  const art = ctx.e(
    'span',
    marble ? 'game-piece game-vessel progressive-vessel' : 'game-piece hanoi-tower',
  );
  art.setAttribute('aria-hidden', 'true');
  if (marble) {
    art.dataset.capacity = '4';
    art.dataset.complete = String(
      cell.layers.length === 4 &&
        cell.layers[0] !== null &&
        cell.layers.every((v) => v === cell.layers[0]),
    );
    for (const [i, colour] of cell.layers.entries()) {
      const piece = ctx.e(
        'span',
        'game-layer game-marble progressive-marble',
        colour === null ? '?' : SHAPES[colour],
      );
      piece.style.setProperty('--layer-index', String(i));
      piece.style.setProperty(
        '--piece-colour',
        colour === null ? 'var(--game-neutral)' : `var(--game-colour-${colour})`,
      );
      piece.dataset.concealed = String(colour === null);
      piece.dataset.top = String(i === cell.layers.length - 1);
      if (colour !== null) piece.dataset.colour = String(colour);
      art.append(piece);
    }
  } else {
    const pole = ctx.e('span', 'hanoi-pole');
    const platform = ctx.e('span', 'hanoi-platform');
    art.append(pole, platform);
    for (const [i, size] of cell.discs.entries()) {
      const disc = ctx.e('span', 'hanoi-disc');
      disc.dataset.disc = String(size);
      disc.dataset.top = String(i === cell.discs.length - 1);
      disc.style.setProperty('--disc-width', `${30 + (size / cell.totalDiscs) * 65}%`);
      disc.style.setProperty('--disc-index', String(i));
      disc.style.setProperty('--piece-colour', `var(--game-colour-${(size - 1) % 8})`);
      art.append(disc);
    }
    art.append(
      ctx.e(
        'span',
        'hanoi-peg-label',
        cell.move.index === 2 ? 'GOAL' : cell.move.index === 0 ? 'START' : 'REST',
      ),
    );
  }
  return art;
}

const flights = new WeakMap();
/** Cancel only presentation, revealing the already accepted domain position. */
export function cancelHanoi(board) {
  flights.get(board)?.cancel();
}

/** Capture the selected top disc's actual geometry before an accepted transfer. */
export function captureHanoi(board, state) {
  cancelHanoi(board);
  if (
    state.gameId !== 'pebble-balance' ||
    state.data.selected < 0 ||
    document.hidden ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
    return null;
  const drag = board.gameDragHanoi;
  if (drag?.from === state.data.selected && drag.seed === state.seed) return drag;
  const actual = [...board.querySelectorAll(`[data-cell="${state.data.selected}"] .hanoi-disc`)].at(
    -1,
  );
  return actual
    ? {
        from: state.data.selected,
        size: actual.dataset.disc,
        rect: actual.getBoundingClientRect(),
        ghost: actual.cloneNode(true),
      }
    : null;
}

/** Lift over the poles, glide horizontally, then settle onto the real stack.
 * A single finite transform animation preserves board geometry. Hidden tab,
 * reduced motion, cancellation and rejection never defer a domain action. */
export function animateHanoi(board, previous, state, capture) {
  cancelHanoi(board);
  if (
    !capture ||
    state.gameId !== 'pebble-balance' ||
    state.data.selected !== -1 ||
    document.hidden ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
    return null;
  const to = state.data.pegs.findIndex((peg, i) => peg.length > previous.data.pegs[i].length);
  if (to < 0) return null;
  const target = board.querySelector(`[data-cell="${to}"] [data-disc="${capture.size}"]`);
  if (!target || typeof capture.ghost.animate !== 'function') return null;
  const origin = board.getBoundingClientRect(),
    end = target.getBoundingClientRect();
  const top =
    Math.min(
      ...[...board.querySelectorAll('.hanoi-pole')].map((pole) => pole.getBoundingClientRect().top),
    ) -
    end.height -
    12;
  const ghost = capture.ghost;
  ghost.classList.add('hanoi-flight');
  ghost.style.left = `${end.left - origin.left}px`;
  ghost.style.top = `${end.top - origin.top}px`;
  ghost.style.width = `${end.width}px`;
  ghost.style.height = `${end.height}px`;
  board.append(ghost);
  target.style.visibility = 'hidden';
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  let animation,
    cleaned = false;
  const clean = () => {
    if (cleaned) return;
    cleaned = true;
    target.style.visibility = '';
    ghost.remove();
    document.removeEventListener('visibilitychange', suspend);
    media.removeEventListener?.('change', suspend);
    if (flights.get(board) === controller) flights.delete(board);
  };
  const controller = {
    cancel() {
      animation?.cancel();
      clean();
    },
    finished: null,
  };
  const suspend = () => {
    if (document.hidden || media.matches) controller.cancel();
  };
  flights.set(board, controller);
  document.addEventListener('visibilitychange', suspend);
  media.addEventListener?.('change', suspend);
  const x = capture.rect.left - end.left,
    y = capture.rect.top - end.top,
    lift = top - end.top;
  try {
    animation = ghost.animate(
      [
        { transform: `translate(${x}px, ${y}px)`, offset: 0 },
        { transform: `translate(${x}px, ${lift}px)`, offset: 0.28, easing: 'ease-in-out' },
        { transform: `translate(0px, ${lift}px)`, offset: 0.72, easing: 'ease-in' },
        { transform: 'translate(0px, 0px)', offset: 1 },
      ],
      { duration: 520, easing: 'ease-out' },
    );
    controller.finished = animation.finished.catch(() => {}).finally(clean);
  } catch {
    clean();
    controller.finished = Promise.resolve();
  }
  return controller;
}
